import React, { useCallback, useContext, useEffect, useState, useReducer } from 'react'
import { useNavigate, useParams } from 'react-router'
import { BillingListDataRooms, BillingListBillingIDs, BillingBillingIDInfo, BillingInviteBillingID, BillingEditUserRel, BillingEditBillingID, BillingResetPassword } from 'modules/Rest'
import Layout from './Layout'
import GridContainer from './GridContainer'
import GridCard from './GridCard'
import CardHeader from './CardHeader'
import { MessageContext } from './Contexts'
import { LoadingDotsIcon } from './Icons'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AddUsersDialog } from './AddUsersDialog'
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft'
import AddIcon from '@mui/icons-material/AddCircleOutline'
import AButton from './AButton'
import StartTaskButton from './StartTaskButton'
import { format } from 'date-fns'
import ActionButtons, { DirectActionButton } from './ActionButtons'
import { setModes } from 'modules/Utils'

const useStyles = makeStyles(() => ({
  tableRow: {
    '& th:not(:first-child):not(:last-child)': { textAlign: 'center' },
    '& td:not(:first-child):not(:last-child)': { textAlign: 'center' },
  },
  loadingDots: {
    paddingBottom: 10,
    paddingLeft: 10,
    display: 'inline-block',
  },
  dateCell: {
    whiteSpace: 'nowrap',
  },
  infoCell: {
    textAlign: 'right',
  },
  btnCell: {
    textAlign: 'right',
    paddingRight: 0,
    '&:last-child': {
      paddingRight: 0,
    },
    paddingTop: 1,
    paddingBottom: 1,
  },
  headingCell: {
    fontWeight: 500,
  },
  showMoreContainer: {
    marginTop: 15,
    textAlign: 'center',
  },
  loadingMoreBottomContainer: {
    marginTop: 15,
    textAlign: 'center',
  },
  wMargin: {
    marginTop: 25,
  },
  noUsers: {
    fontStyle: 'italic',
  },
  dialogContent: {
    width: 400,
  },
}))

export default function Billing() {
  const { billingId = '' } = useParams()
  const classes = useStyles()
  const navigate = useNavigate()
  const { messageDispatch } = useContext(MessageContext)
  const [billingIDs, setBillingIDs] = useState([])
  const [loadingDataRooms, setLoadingDataRooms] = useState(false)
  const [loadingBillingIDs, setLoadingBillingIDs] = useState(false)
  const [loadingBillingIDInfo, setLoadingBillingIDInfo] = useState(false)
  const [billingIDInfo, setBillingIDInfo] = useState(null)
  const [modeAddUsers, setModeAddUsers] = useState('')
  const [modeResetPasswords, setModeResetPasswords] = useState('')

  const cursorReducer = useCallback((prevCursor, action) => {
    switch (action.type) {
      case 'reset':
        return null
      case 'set':
        if (action.billing_id === billingId) {
          return action.cursor
        } else {
          return prevCursor
        }
      default:
        throw new Error()
    }
  }, [billingId])
  const [dataRoomCursor, cursorDispatch] = useReducer(cursorReducer, null)

  const dataRoomsReducer = useCallback((prevDataRooms, action) => {
    switch (action.type) {
      case 'reset':
        return []
      case 'add':
        if (action.billing_id === billingId) {
          return [...prevDataRooms, ...action.data_rooms]
        } else {
          return prevDataRooms
        }
      default:
        throw new Error()
    }
  }, [billingId])
  const [dataRooms, dataRoomsDispatch] = useReducer(dataRoomsReducer, [])

  const getDataRooms = useCallback(async (cursor=null, seen_data_rooms=[]) => {
    try {
      setLoadingDataRooms(true)
      const { data: { success, info, data_rooms, cursor: newCursor } } = await BillingListDataRooms({cursor, seen_data_rooms, billing_id: billingId, max_num: 20})
      if (success) {
        dataRoomsDispatch({type: "add", billing_id: billingId, data_rooms: data_rooms})
        cursorDispatch({type: "set", billing_id: billingId, cursor: newCursor ? newCursor : null})

        if (newCursor && (data_rooms.length + seen_data_rooms.length) < 40) {
          await getDataRooms(newCursor, [...seen_data_rooms, ...data_rooms.map((data_room_i) => (data_room_i.data_room))])
        }
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setLoadingDataRooms(false)
  }, [messageDispatch, billingId])

  const getMoreDataRooms = () => {
    getDataRooms(dataRoomCursor, dataRooms.map((data_room_i) => (data_room_i.data_room)))
  }

  const getBillingIDs = useCallback(async () => {
    try {
      setLoadingBillingIDs(true)
      const { data: { success, info, billing_ids } } = await BillingListBillingIDs()
      if (success) {
        setBillingIDs(billing_ids.sort((a,b) =>  a.billing_id.toUpperCase() >= b.billing_id.toUpperCase() ? 1 : -1))
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setLoadingBillingIDs(false)
  }, [messageDispatch])

  const getBillingIDInfo = useCallback(async () => {
    if (billingId) {
      try {
        setLoadingBillingIDInfo(true)
        const { data: { success, info, ...data } } = await BillingBillingIDInfo({ billing_id: billingId })
        if (success) {
          setBillingIDInfo(data)
        } else {
          throw new Error(info ? info : 'An error occurred')
        }
      } catch (error) {
        messageDispatch({ type: 'warning', payload: error.message })
      }
      setLoadingBillingIDInfo(false)
    } else {
      setBillingIDInfo(null)
    }
  }, [messageDispatch, billingId])

  const removeUser = useCallback(async user_name => {
    try {
      const { data: { success, info } } = await BillingEditUserRel({user_name, billing_id: billingIDInfo.billing_id, remove: true})
      if (success) {
        getBillingIDInfo()
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [billingIDInfo, getBillingIDInfo, messageDispatch])

  const editBillingID = useCallback(async ({ enforce_tfa }) => {
    try {
      const { data: { success, info, ...data } } = await BillingEditBillingID({enforce_tfa, billing_id: billingIDInfo.billing_id})
      if (success) {
        setBillingIDInfo(data)
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [billingIDInfo, messageDispatch])

  const resetPassword = useCallback(async ({ billing_id, user }) => {
    try {
      setModeResetPasswords('pending')
      const { data: { success, info } } = await BillingResetPassword({billing_id, user})
      if (success) {
        await setModes(setModeResetPasswords, ['completing', 1500, ''])
        messageDispatch({type: 'info', payload: 'The action has been triggered. All users will be required to change their password the next time they log in.'})
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeResetPasswords('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [messageDispatch])

  const showBillingID = function(billing_id) {
    dataRoomsDispatch({type: "reset"})
    cursorDispatch({type: "reset"})
    navigate(`/billing/${billing_id}`)
  }

  const getLogoURL = (billing_id) => {
    return `${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/billing/billing_id_logo?billing_id=${billing_id}`
  }

  useEffect(() => {
    dataRoomsDispatch({type: "reset"})
    cursorDispatch({type: "reset"})
    getDataRooms()
  }, [getDataRooms])

  useEffect(() => {
    getBillingIDs()
  }, [getBillingIDs])

  useEffect(() => {
    getBillingIDInfo()
  }, [getBillingIDInfo])

  return <Layout showRoomSelect={false}>
    <GridContainer>
      {!billingId && <GridCard sm={12} uniformPadding={true}>
        <CardHeader
          title={<>
            <>Billing IDs</>
            {loadingBillingIDs ? <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div> : null}
          </>}
          headingElement='h5'
        />
        {!loadingBillingIDs || (billingIDs && billingIDs.length) ? <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow className={classes.tableRow}>
                <TableCell>Billing ID</TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingIDs.map((billingID) => (
                <TableRow key={billingID.billing_id} className={classes.tableRow}>
                  <TableCell>
                    {billingID.billing_id}
                  </TableCell>
                  <TableCell className={classes.infoCell}>
                    <AButton onClick={() => showBillingID(billingID.billing_id)}>
                      More info
                    </AButton>
                  </TableCell>
                </TableRow>))}
            </TableBody>
          </Table>
        </TableContainer> : null}
      </GridCard>}

      {billingId && <GridCard sm={12} uniformPadding={true}>
        <CardHeader
          title={<>
            <>Billing ID: {billingId}</>
            {loadingBillingIDInfo ? <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div> : null}
          </>}
          headingElement='h5'
          buttons={[<AButton
            startIcon={<SubdirectoryArrowLeftIcon/>}
            onClick={() => navigate('/billing')}
            style={{ float: 'right' }}
          >
            Back
          </AButton>]}
        />

        {billingIDInfo && <TableContainer>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell className={classes.headingCell}>Custom logo</TableCell>
                <TableCell><img src={getLogoURL(billingId)} style={{height: 50}} alt=''/></TableCell>
                <TableCell/>
              </TableRow>

              <TableRow>
                <TableCell className={classes.headingCell}>Users can create standard data rooms</TableCell>
                <TableCell>{billingIDInfo.allow_full ? 'Yes' : 'No'}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Users can create Light Data Rooms</TableCell>
                <TableCell>{billingIDInfo.allow_light ? 'Yes' : 'No'}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Users can activate the template editor</TableCell>
                <TableCell>{billingIDInfo.allow_template_editor ? 'Yes' : 'No'}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>PDF documents can be redacted in data rooms</TableCell>
                <TableCell>{billingIDInfo.allow_redaction ? 'Yes' : 'No'}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Enforce two-factor authentication for all users of Billing ID</TableCell>
                <TableCell>{billingIDInfo.enforce_tfa ? 'Yes' : 'No'}</TableCell>
                <TableCell className={classes.btnCell}>
                  {billingIDInfo.allow_enforce_tfa && <DirectActionButton actionTitle={billingIDInfo.enforce_tfa ? 'Disable' : 'Enable'} action={() => editBillingID({enforce_tfa: !billingIDInfo.enforce_tfa})} extraModes={['completing', 1500]}/>}
                </TableCell>
              </TableRow>
              {billingIDInfo.allow_password_reset && <TableRow>
                <TableCell className={classes.headingCell}>Force all users of Billing ID to change their password</TableCell>
                <TableCell/>
                <TableCell className={classes.btnCell}>
                  <AButton onClick={() => setModeResetPasswords('editing')} >Force password reset</AButton>
                </TableCell>
              </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>}

        <Dialog open={!!modeResetPasswords}>
          <DialogTitle>Confirmation request</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Typography variant="body2">
              Would you like to force all users of the Billing ID to change their password the next time they log in?
            </Typography>
            <Typography variant="body2">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <ActionButtons
              mode={modeResetPasswords}
              setMode={setModeResetPasswords}
              action={() => {
                resetPassword({billing_id: billingIDInfo.billing_id})
              }}
              cancel={(e, setMode) => setMode('')}
              actionTitle="Force reset"
              colorAction="error"
            />
          </DialogActions>
        </Dialog>

        {billingIDInfo && (loadingBillingIDs || billingIDInfo.allow_add_edit_users || (billingIDInfo.users && billingIDInfo.users.length)) ? <TableContainer>
          <Table size='small' className={classes.wMargin}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Tooltip title='Users assigned to the billing ID can create data rooms on behalf of the billing ID' placement='top-start'>
                    <span>
                      Users
                    </span>
                  </Tooltip>
                </TableCell>
                <Tooltip title={billingIDInfo.allow_add_edit_users ? '' : 'The action has not been enabled'} placement="top-end">
                  <TableCell className={classes.btnCell}>
                    <AButton startIcon={<AddIcon/>} onClick={() => setModeAddUsers('editing')} disabled={!billingIDInfo.allow_add_edit_users}>Add users</AButton>
                  </TableCell>
                </Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingIDInfo.users.length ? billingIDInfo.users.map((user) => (
                <TableRow>
                  <TableCell>{user.user_name}</TableCell>
                  <TableCell className={classes.btnCell}>
                    {user.more_info && <AButton onClick={() => navigate(`/billing-user/${user.user_name}`)} style={{marginRight: 10}}>More info</AButton>}
                    {billingIDInfo.allow_add_edit_users && <DirectActionButton actionTitle='Remove' color='error' action={() => removeUser(user.user_name)} extraModes={['completing', 1500]}/>}
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell className={classes.noUsers}>No users</TableCell><TableCell/></TableRow>}
            </TableBody>
          </Table>
        </TableContainer> : null}

        <AddUsersDialog
          mode={modeAddUsers}
          setMode={setModeAddUsers}
          callback={getBillingIDInfo}
          inviteFn={BillingInviteBillingID}
          params={{billing_id: billingId}}
        />

        {billingIDInfo && (loadingBillingIDs || (billingIDInfo.billing_admins && billingIDInfo.billing_admins.length)) ? <TableContainer>
          <Table size='small' className={classes.wMargin}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Tooltip title='Billing admins can add new users to the billing ID and invite and remove administrators from all data rooms assigned to the billing ID' placement='top-start'>
                    <span>
                      Billing admins
                    </span>
                  </Tooltip>
                </TableCell>
                <Tooltip title='Contact Aversure support to add/remove billing admins' placement='top-end'>
                  <TableCell className={classes.btnCell}>
                    <AButton startIcon={<AddIcon/>} disabled={true}>Add billing admins</AButton>
                  </TableCell>
                </Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingIDInfo.billing_admins.length ? billingIDInfo.billing_admins.map((user) => (
                <TableRow>
                  <TableCell>{user}</TableCell>
                  <TableCell/>
                </TableRow>
              )) : <TableRow><TableCell className={classes.noUsers}>No billing admins</TableCell><TableCell/></TableRow>}
            </TableBody>
          </Table>
        </TableContainer> : null}
      </GridCard>}

      {loadingDataRooms || (dataRooms && dataRooms.length) ? <GridCard sm={12} uniformPadding={true}>
        <CardHeader
          title={<>
            <>Data Rooms</>
            {loadingDataRooms ? <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div> : null}
          </>}
          headingElement='h5'
          buttons={[<StartTaskButton
            args={{billing_id: billingId}}
            url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/billing/export?use_async=true`}
            stage_label='Requesting data room export'
            buttonText='Download list'
          />]}
        />
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow className={classes.tableRow}>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell className={classes.dateCell}>Created</TableCell>
                <TableCell className={classes.dateCell}>Last renewal</TableCell>
                <TableCell>Billing ID</TableCell>
                <TableCell/>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRooms && dataRooms.map((dataRoom) => (
                dataRoom && <TableRow key={dataRoom.data_room} className={classes.tableRow}>
                  <TableCell>
                    {dataRoom.data_room_display_name}
                    {dataRoom && dataRoom.light && <span style={{ fontStyle: 'italic' }}> Light</span>}
                  </TableCell>
                  <TableCell>
                    {dataRoom.deleted ? 'Deleted' : (dataRoom.disabled ? 'Disabled' : 'Enabled')}
                  </TableCell>
                  <TableCell className={classes.dateCell}>
                    {format(dataRoom.creation_time, 'yyyy-MM-dd')}
                  </TableCell>
                  <TableCell className={classes.dateCell}>
                    {format(dataRoom.renewal_time, 'yyyy-MM-dd')}
                  </TableCell>
                  <TableCell>
                    {dataRoom.billing_id}
                  </TableCell>
                  <TableCell className={classes.infoCell}>
                    {dataRoom.export_stats && <StartTaskButton
                      args={dataRoom}
                      url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/stats/export?use_async=true`}
                      stage_label="Requesting stats"
                      buttonText="Export stats"
                    />}
                  </TableCell>
                  <TableCell className={classes.infoCell}>
                    <AButton onClick={() => navigate(`/billing-data-room/${dataRoom.data_room}`)}>More info</AButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loadingDataRooms && dataRooms && dataRooms.length > 30 && <div className={classes.loadingMoreBottomContainer}>
          <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div>
        </div>}
        {!loadingDataRooms && dataRoomCursor && <div className={classes.showMoreContainer}>
          <AButton onClick={getMoreDataRooms}>Show more data rooms</AButton>
        </div>}
      </GridCard> : null}
    </GridContainer>
  </Layout>
}
